import { useNavigate } from "react-router-dom";
import {
  LayoutDashboard, Users, Radio, Server, Tv, Activity, Clock,
  Plus, Upload, RefreshCw, AlertTriangle, FileText,
} from "lucide-react";
import AdminLayout from "@/components/Layout";
import PageHeader from "@/components/admin/PageHeader";
import StatusChip from "@/components/admin/StatusChip";
import EmptyState from "@/components/admin/EmptyState";
import Button from "@/components/admin/Button";

/**
 * Dashboard — clean, API-ready shell.
 *
 * No charts, no gauges, no sparklines, no fake telemetry.
 * All numeric metrics default to "—"; list sections default to empty states.
 * Replace the `metrics` / list data with real API responses when wiring.
 */
export default function Dashboard() {
  const navigate = useNavigate();

  // Placeholder shape — mirrors the fields we expect from backend.
  // Values intentionally "—" until API is connected.
  const metrics = {
    active_customers: "—",
    active_lines:     "—",
    online_sessions:  "—",
    channels_online:  "—",
    channels_total:   "—",
    nodes_online:     "—",
    nodes_total:      "—",
  };

  return (
    <AdminLayout breadcrumb={["Operasyon", "Pano"]} title="Pano">
      <div className="px-6 lg:px-10 py-8 space-y-8">
        <PageHeader
          icon={LayoutDashboard}
          kicker="Operasyon"
          title="Sistem Panosu"
          description="Sistem sağlığı, aktif oturumlar, node durumu, yaklaşan süreler ve son olaylara hızlı bakış."
          actions={<Button variant="outline" size="sm" icon={RefreshCw}>Yenile</Button>}
        />

        {/* 1) System Health Summary */}
        <Section title="Sistem Sağlığı" subtitle="Özet göstergeler">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <MetricCard label="Müşteri"      value={metrics.active_customers} icon={Users} />
            <MetricCard label="Aktif Hat"    value={metrics.active_lines}     icon={Radio} />
            <MetricCard label="Online Oturum" value={metrics.online_sessions} icon={Activity} />
            <MetricCard
              label="Kanal"
              value={metrics.channels_online !== "—" ? `${metrics.channels_online}/${metrics.channels_total}` : "—"}
              icon={Tv}
            />
          </div>
        </Section>

        {/* 2) Active Sessions + 3) Node Health — paired row */}
        <div className="grid lg:grid-cols-2 gap-6">
          <Section
            title="Aktif Oturumlar"
            subtitle="Şu an izleme yapan hatlar"
            action={<Button variant="outline" size="sm" onClick={() => navigate("/admin/analytics")}>Detaylı Rapor</Button>}
          >
            <div className="rounded-lg border border-border bg-surface">
              <EmptyState
                icon={Activity}
                title="Oturum verisi yok"
                description="Backend bağlandığında canlı oturumlar burada listelenir."
              />
            </div>
          </Section>

          <Section
            title="Node Sağlığı"
            subtitle="Edge sunucu durumu"
            action={<Button variant="outline" size="sm" onClick={() => navigate("/admin/nodes")}>Tümünü Gör</Button>}
          >
            <div className="rounded-lg border border-border bg-surface">
              <EmptyState
                icon={Server}
                title="Node verisi yok"
                description="Backend bağlandığında node havuzu burada görünür."
              />
            </div>
          </Section>
        </div>

        {/* 4) Recent Events + 5) Expiring Subscriptions — paired row */}
        <div className="grid lg:grid-cols-3 gap-6">
          <Section
            title="Son Olaylar"
            subtitle="Sistem aktivite kaydı"
            className="lg:col-span-2"
            action={<Button variant="outline" size="sm" icon={FileText}>Tüm Günlükler</Button>}
          >
            <div className="rounded-lg border border-border bg-surface">
              <EmptyState
                icon={AlertTriangle}
                title="Henüz olay yok"
                description="Sistem olayları (login, failover, abonelik değişikliği, kaynak hataları) burada gösterilir."
              />
            </div>
          </Section>

          <Section
            title="Yaklaşan Süresi Bitimleri"
            subtitle="7 gün içinde"
            action={<StatusChip tone="neutral">—</StatusChip>}
          >
            <div className="rounded-lg border border-border bg-surface">
              <EmptyState
                icon={Clock}
                title="Yaklaşan süre yok"
                description="Süresi dolmak üzere olan hatlar burada listelenir."
              />
            </div>
          </Section>
        </div>

        {/* 6) Quick Actions */}
        <Section title="Hızlı İşlemler" subtitle="Sık kullanılan yönetim kısayolları">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <QuickAction
              icon={Plus}
              label="Yeni Müşteri"
              hint="Abonelik oluştur"
              onClick={() => navigate("/admin/customers")}
            />
            <QuickAction
              icon={Radio}
              label="Yeni Hat"
              hint="Hat kimlik bilgileri"
              onClick={() => navigate("/admin/lines")}
            />
            <QuickAction
              icon={Upload}
              label="Kanal İçe Aktar"
              hint="M3U / Xtream"
              onClick={() => navigate("/admin/channels")}
            />
            <QuickAction
              icon={Server}
              label="Node Ekle"
              hint="Load balancer havuzu"
              onClick={() => navigate("/admin/nodes")}
            />
          </div>
        </Section>
      </div>
    </AdminLayout>
  );
}

/* ---------- Local helpers (file-scoped, no new component files) ---------- */

function Section({ title, subtitle, action, children, className = "" }) {
  return (
    <section className={className}>
      <div className="flex items-end justify-between mb-3 gap-4">
        <div>
          <h2 className="font-display text-lg font-semibold">{title}</h2>
          {subtitle && (
            <div className="text-[11px] font-mono text-ink-dim uppercase tracking-wider mt-0.5">{subtitle}</div>
          )}
        </div>
        {action}
      </div>
      {children}
    </section>
  );
}

function MetricCard({ label, value, icon: Icon }) {
  return (
    <div className="p-5 rounded-lg border border-border bg-surface">
      <div className="flex items-center justify-between mb-3">
        {Icon && <Icon className="w-4 h-4 text-brand" />}
      </div>
      <div className="font-display text-3xl font-bold leading-none">{value}</div>
      <div className="text-[11px] font-mono uppercase tracking-widest text-ink-dim mt-2">{label}</div>
    </div>
  );
}

function QuickAction({ icon: Icon, label, hint, onClick }) {
  return (
    <button
      onClick={onClick}
      className="p-4 rounded-lg border border-border bg-surface text-left hover:border-brand/50 hover:bg-white/5 transition"
    >
      <div className="w-9 h-9 rounded-md bg-brand/10 border border-brand/30 grid place-items-center mb-3">
        <Icon className="w-4 h-4 text-brand" />
      </div>
      <div className="font-display font-semibold text-sm">{label}</div>
      <div className="text-[11px] font-mono text-ink-dim mt-0.5">{hint}</div>
    </button>
  );
}
